import FormControlLabel from '@mui/material/FormControlLabel';
import React, { useState, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { styled } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { TreeViewProps } from '@mui/lab';

import Box from '../Box/Box';
import Input from '../Input/Input';
import Icon from '../Icon/Icon';
import Typography from '../Typography/Typography';

import TreeLayout from './TreeLayout';
import useTreeData, { Item } from './useTreeData';

type Props = {
  treeData: Item[];
  onSelectItems?: (data: Item[], isAllItems?: boolean) => void;
  layout?: 'simple' | 'default';
  title: string;
  isAllItems?: boolean;
} & TreeViewProps;

const BoxWrapperStyled = styled(Box)(({ theme }) => {
  return {
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey.A700}`,
    padding: theme.spacing(2),
    minHeight: 600
  };
});

export const ItemsTree = ({
  treeData,
  onSelectItems = () => {},
  isAllItems,
  title,
  layout = 'default',
  ...otherProps
}: Props) => {
  const onInnerSelectItems = (data: Item[]) => {
    onSelectItems(data, isAllToggled);
  };

  const [filterQuery, setFilterQuery] = useState('');
  const [isAllToggled, setIsAllToggled] = useState(false);
  const { api, data } = useTreeData(treeData, onInnerSelectItems, filterQuery, isAllToggled);
  const [expanded, setExpanded] = useState<string[]>([]);

  useEffect(() => {
    setExpanded(treeData.map(({ id }) => String(id)));
  }, [treeData]);

  useEffect(() => {
    if (typeof isAllItems !== 'undefined' && isAllItems !== null) {
      setIsAllToggled(!!isAllItems);
    }
  }, [isAllItems]);

  return (
    <Box>
      <Typography variant="body2" lineHeight="40px" color="text.primary">
        {title}
      </Typography>
      <BoxWrapperStyled>
        <Stack>
          <Box sx={{ padding: '6px 0' }}>
            <FormControl>
              <RadioGroup row name="items-tree-mode">
                <FormControlLabel
                  sx={{ marginLeft: '5px' }}
                  labelPlacement="start"
                  value={`all_${title}`}
                  control={<Radio />}
                  label={`All ${title}`}
                  onChange={(_, value: boolean) => {
                    if (value) {
                      setIsAllToggled(true);
                      onSelectItems([], value);
                    }
                  }}
                />
                <FormControlLabel
                  sx={{ marginLeft: '56px' }}
                  onChange={(_, value: boolean) => {
                    if (value) {
                      setIsAllToggled(false);
                      api.toggleAllTree(false);
                    }
                  }}
                  labelPlacement="start"
                  value="custom"
                  control={<Radio />}
                  label="Custom"
                />
              </RadioGroup>
            </FormControl>
          </Box>
          <Box sx={{ padding: '10px 5px' }}>
            <Input
              fullWidth={true}
              disabled={isAllToggled}
              placeholder="Search..."
              value={filterQuery}
              type="text"
              onChange={event => {
                setFilterQuery(event?.target?.value);
              }}
              startAdornment={
                <InputAdornment position="start">
                  <Icon name="search" />
                </InputAdornment>
              }
            />
          </Box>
          <TreeLayout
            items={data.itemsMap}
            parentsMap={data.parentsMap}
            layout={layout}
            onSelectItems={onInnerSelectItems}
            filterQuery={filterQuery}
            getChildren={api.getChildren}
            isParent={api.isParent}
            getData={api.getData}
            toggleItem={api.toggleItem}
            hasSomeDescendantSelected={api.hasSomeDescendantSelected}
            hasAllChildrenSelected={api.hasAllChildrenSelected}
            toggleBranch={api.toggleBranch}
            toggleAllChildren={api.toggleAllChildren}
            isTreeFiltered={api.isTreeFiltered}
            expanded={expanded}
            {...otherProps}
          />
        </Stack>
      </BoxWrapperStyled>
    </Box>
  );
};

export default ItemsTree;
